import { computed } from "mobx";
import {
  _async,
  _await,
  ExtendedModel,
  getRootStore,
  model,
  modelAction,
  modelFlow,
  prop,
} from "mobx-keystone";
import { RootStore } from "..";
import api from "../../api";
import {
  APIRequirementDetail,
  APIRequirementInput,
} from "../../api/requirements";
import DirtyableWithId from "./DirtyableWithId";

@model("collab/Requirement")
class Requirement extends ExtendedModel(DirtyableWithId, {
  project: prop<identifier>(),
  uniqueRef: prop<string>(""),
  destinationCountry: prop<identifier | undefined>(),
  destinationCountryName: prop<string | null>(null),
  destinationClusterName: prop<string | null>(null),
  deliveryTerms: prop<identifier | undefined>(),
  deliveryTermsName: prop<string | null>(null),
  deliveryLocation: prop<string>(""),
  productSpecification: prop<string>(""),
  packSize: prop<identifier | undefined>(),
  packSizeName: prop<string | null>(null),
  packageType: prop<string>(""),
  volumeYear1: prop<number | null>(),
  volumeYear2: prop<number | null>(),
  volumeYear3: prop<number | null>(),
  volumeYear4: prop<number | null>(),
  volumeYear5: prop<number | null>(),
  packSizeRfq: prop<string>(""),
  altScenario: prop<string>(""),
  isCltLocked: prop<boolean>(false),
}) {
  @computed
  get asAPI() {
    const apiData: APIRequirementInput = {
      uniqueRef: this.uniqueRef,
      destinationCountry: this.destinationCountry,
      deliveryTerms: this.deliveryTerms,
      deliveryLocation: this.deliveryLocation,
      productSpecification: this.productSpecification,
      packSize: this.packSize,
      packageType: this.packageType,
      volumeYear1:
        (this.volumeYear1 as unknown as string) === ""
          ? null
          : this.volumeYear1,
      volumeYear2:
        (this.volumeYear2 as unknown as string) === ""
          ? null
          : this.volumeYear2,
      volumeYear3:
        (this.volumeYear3 as unknown as string) === ""
          ? null
          : this.volumeYear3,
      volumeYear4:
        (this.volumeYear4 as unknown as string) === ""
          ? null
          : this.volumeYear4,
      volumeYear5:
        (this.volumeYear5 as unknown as string) === ""
          ? null
          : this.volumeYear5,
      packSizeRfq: this.packSizeRfq,
      altScenario: this.altScenario,
    };
    if (this.id > 0) {
      apiData.id = this.id;
    }
    return apiData;
  }

  @computed
  get asRow() {
    return {
      id: this.id,
      uniqueRef: this.uniqueRef,
      destinationCluster: this.destinationCluster,
      destinationCountry: this.destinationCountry,
      deliveryLocation: this.deliveryLocation,
      deliveryTerms: this.deliveryTerms,
      productSpecification: this.productSpecification,
      packSize: this.packSize,
      packageType: this.packageType,
      volumeYear1: this.volumeYear1,
      volumeYear2: this.volumeYear2,
      volumeYear3: this.volumeYear3,
      volumeYear4: this.volumeYear4,
      volumeYear5: this.volumeYear5,
      packSizeRfq: this.packSizeRfq,
      altScenario: this.altScenario,
    };
  }

  @computed
  get asCSV() {
    return {
      uniqueRef: this.uniqueRef,
      destinationCluster: this.destinationCluster,
      // Always use DB-cached names — they are kept in sync by setValue() and by
      // the backend Requirement.save(), so they correctly reflect the value at the
      // time the row was last saved (not a potentially renamed live asset name).
      destinationCountry: this.destinationCountryName || "",
      deliveryLocation: this.deliveryLocation,
      deliveryTerms: this.deliveryTermsName || "",
      productSpecification: this.productSpecification,
      packSize: this.packSizeName || "",
      packageType: this.packageType,
      volumeYear1: this.volumeYear1,
      volumeYear2: this.volumeYear2,
      volumeYear3: this.volumeYear3,
      volumeYear4: this.volumeYear4,
      volumeYear5: this.volumeYear5,
      packSizeRfq: this.packSizeRfq,
      altScenario: this.altScenario,
    };
  }

  @computed
  get destinationCluster() {
    const store = getRootStore<RootStore>(this);
    if (!this.destinationCountry) {
      // Fall back to stored snapshot cluster name if no country ID
      return this.destinationClusterName || "";
    }
    const country = store?.assets?.countryListItems?.get(
      Number(this.destinationCountry)
    );
    if (!country) {
      // Country was deleted — use the name stored in the snapshot
      return this.destinationClusterName || "";
    }
    const cluster = store?.assets?.clusterListItems?.get(country.cluster);
    if (!cluster) {
      return this.destinationClusterName || "";
    }
    return cluster.name;
  }

  private _findValue(options: { value: number; label: string }[], value: any): any {
    // Name-based lookup: only called when the value is a string (not an ID).
    // Prevents accidental ID collisions when multiple options have the same name.
    const valueStr = value?.toString().trim().toLowerCase() || "";
    if (!valueStr) {
      return "";
    }
    
    const matches = options.filter(
      (opt) =>
        opt.label.toString().trim().toLowerCase() === valueStr
    );
    
    if (matches.length === 0) {
      // No match found — return empty string (safer than returning first match)
      console.warn(
        `[Requirement._findValue] No option found for label "${value}". Returning empty string.`
      );
      return "";
    }
    
    if (matches.length > 1) {
      // Multiple matches — log a warning to help detect data issues
      console.warn(
        `[Requirement._findValue] Multiple options found for label "${value}":`,
        matches.map((m) => `(id=${m.value}, label="${m.label}")`)
      );
    }
    
    return matches[0].value;
  }

  @modelAction
  setValue(key: string, value: any) {
    const store = getRootStore<RootStore>(this);
    let v = value;
    if (store && key === "destinationCountry") {
      // Never do name-based lookup for numeric IDs — preserve the actual ID even if the
      // asset was deleted, to avoid accidentally assigning the first matching country name.
      // This prevents AU003 and AU004 from accidentally sharing the same country.
      if (typeof value === "number" || (typeof value === "string" && !isNaN(Number(value)))) {
        v = Number(value);
      } else if (typeof value === "string" && value.trim() !== "") {
        // Only do name-based lookup for string values
        v = this._findValue(store.assets.countrySelect, value);
      } else {
        v = value; // null or empty string — pass through
      }
      // Keep name caches in sync so the display always shows the selected name
      // (not the live asset name which may have changed since the row was last saved).
      const country = v ? store.assets.countryListItems.get(Number(v)) : undefined;
      this.destinationCountryName = country?.name ?? null;
      const cluster = country?.cluster
        ? store.assets.clusterListItems.get(country.cluster)
        : undefined;
      this.destinationClusterName = cluster?.name ?? null;
    } else if (store && key === "deliveryTerms") {
      if (typeof value === "number" || (typeof value === "string" && !isNaN(Number(value)))) {
        v = Number(value);
      } else if (typeof value === "string" && value.trim() !== "") {
        v = this._findValue(store.assets.termsSelect, value);
      } else {
        v = value;
      }
      // Keep name cache in sync
      const terms = v ? store.assets.termsListItems.get(Number(v)) : undefined;
      this.deliveryTermsName = terms?.name ?? null;
    } else if (store && key === "packSize") {
      if (typeof value === "number" || (typeof value === "string" && !isNaN(Number(value)))) {
        v = Number(value);
      } else if (typeof value === "string" && value.trim() !== "") {
        v = this._findValue(store.assets.packSizeSelect, value);
      } else {
        v = value;
      }
      // Keep name cache in sync
      const packSize = v ? store.assets.packSizeListItems.get(Number(v)) : undefined;
      this.packSizeName = packSize?.name ?? null;
    } else if (
      key === "volumeYear1" ||
      key === "volumeYear2" ||
      key === "volumeYear3" ||
      key === "volumeYear4" ||
      key === "volumeYear5"
    ) {
      v = v ? v : null;
    }
    super.setValue(key, v);
  }

  @modelAction
  update(data: APIRequirementDetail) {
    const store = getRootStore<RootStore>(this);
    if (store) {
      store.requirements.listItems.delete(this.id);
      super.update(data);
      store.requirements.listItems.set(data.id, this);
    }
  }

  @modelFlow
  save = _async(function* (this: Requirement) {
    if (!this.isDirty) {
      return;
    }

    if (!this.uniqueRef) {
      return;
    }

    let response: APIRequirementDetail;
    try {
      if (this.id < 0) {
        response = yield* _await(
          api.requirements.create(this.project, this.asAPI)
        );
      } else {
        response = yield* _await(api.requirements.patch(this.id, this.asAPI));
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
    this.update(response);

    yield null;
  });
}

export default Requirement;
